import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isBefore, startOfDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import { CalendarEvent, Holiday } from '../types/api';

// Google Calendar API 설정
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY;
const KOREA_HOLIDAY_CALENDAR_ID = 'ko.south_korea%23holiday%40group.v.calendar.google.com';

// 공휴일 정보 타입 export
export interface PublicHolidayInfo {
  name: string;
  isPublicHoliday: boolean;
}

interface SimpleCalendarProps {
  events: CalendarEvent[];
  holidays?: Holiday[];
  onDateClick?: (date: Date, dayEvents: CalendarEvent[]) => void;
  onMonthChange?: (date: Date) => void;
  selectedDate?: Date | null;
  onPublicHolidaysLoad?: (holidaysMap: Map<string, PublicHolidayInfo>) => void;
  onEventDrop?: (event: CalendarEvent, newDate: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onEventDoubleClick?: (event: CalendarEvent) => void;
  draggableEventTypes?: string[];
  hideHeader?: boolean; // 월 네비게이션을 외부(페이지 헤더)에서 제어할 때
  date?: Date; // 외부에서 제어하는 현재 월
}

const SimpleCalendar: React.FC<SimpleCalendarProps> = ({
  events,
  holidays = [],
  onDateClick,
  onMonthChange,
  selectedDate,
  onPublicHolidaysLoad,
  onEventDrop,
  onEventClick,
  onEventDoubleClick,
  draggableEventTypes = ['regular', 'rescheduled', 'personal'],
  hideHeader = false,
  date,
}) => {
  const [internalDate, setInternalDate] = useState(new Date());
  const currentDate = date ?? internalDate;
  const [isMobile, setIsMobile] = useState(false);
  const [maxEvents, setMaxEvents] = useState(3);
  // { name: 공휴일/기념일 이름, isPublicHoliday: 공휴일 여부 }
  const [publicHolidaysMap, setPublicHolidaysMap] = useState<Map<string, { name: string; isPublicHoliday: boolean }>>(new Map());
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);

  // 화면 크기에 따른 maxEvents 계산
  useEffect(() => {
    const updateDisplaySettings = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);

      // 화면 크기별 maxEvents 설정
      if (width <= 768) {
        setMaxEvents(2); // 모바일: 점 2개
      } else if (width <= 1024) {
        setMaxEvents(2); // 태블릿: 이벤트 2개
      } else if (width <= 1200) {
        setMaxEvents(3); // 소형 데스크톱: 이벤트 3개
      } else {
        setMaxEvents(4); // 대형 데스크톱: 이벤트 4개
      }
    };

    updateDisplaySettings();
    window.addEventListener('resize', updateDisplaySettings);

    return () => window.removeEventListener('resize', updateDisplaySettings);
  }, []);

  // Google Calendar API로 한국 공휴일 가져오기
  const fetchPublicHolidays = useCallback(async (year: number) => {
    if (!GOOGLE_API_KEY) {
      console.warn('Google Calendar API key is not configured');
      return;
    }

    try {
      const timeMin = `${year}-01-01T00:00:00Z`;
      const timeMax = `${year}-12-31T23:59:59Z`;
      const url = `https://www.googleapis.com/calendar/v3/calendars/${KOREA_HOLIDAY_CALENDAR_ID}/events?key=${GOOGLE_API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch public holidays');
      }

      const data = await response.json();
      const holidayMap = new Map<string, { name: string; isPublicHoliday: boolean }>();

      if (data.items) {
        data.items.forEach((event: any) => {
          const name = event.summary;
          const startDate = event.start.date; // "2025-01-01"
          const endDate = event.end?.date;
          // description에 "공휴일"이 포함되어 있으면 공휴일, 아니면 기념일
          const isPublicHoliday = event.description?.includes('공휴일') ?? false;

          // 연휴인 경우 모든 날짜에 추가
          if (endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const current = new Date(start);

            while (current < end) {
              const dateStr = format(current, 'yyyy-MM-dd');
              if (!holidayMap.has(dateStr)) {
                holidayMap.set(dateStr, { name, isPublicHoliday });
              }
              current.setDate(current.getDate() + 1);
            }
          } else {
            holidayMap.set(startDate, { name, isPublicHoliday });
          }
        });
      }

      setPublicHolidaysMap(holidayMap);
    } catch (error) {
      console.error('공휴일 조회 오류:', error);
    }
  }, []);

  // 연도 변경 시 공휴일 데이터 다시 가져오기
  const currentYear = currentDate.getFullYear();
  useEffect(() => {
    fetchPublicHolidays(currentYear);
  }, [currentYear, fetchPublicHolidays]);

  // 공휴일 데이터 로드 완료 시 콜백 호출
  useEffect(() => {
    if (onPublicHolidaysLoad && publicHolidaysMap.size > 0) {
      onPublicHolidaysLoad(publicHolidaysMap);
    }
  }, [publicHolidaysMap, onPublicHolidaysLoad]);

  // 특정 날짜의 공휴일/기념일 정보 가져오기
  const getHolidayInfo = (date: Date): { name: string; isPublicHoliday: boolean } | null => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return publicHolidaysMap.get(dateStr) || null;
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // 달력 그리드를 위한 전체 날짜 배열 생성
  const getCalendarDays = () => {
    const startDate = monthStart;
    const endDate = monthEnd;

    // 첫 번째 주의 시작을 일요일로 맞추기 위해 이전 달의 날짜들 추가
    const firstDayOfWeek = startDate.getDay(); // 0=일요일, 1=월요일, 2=화요일...
    const prevMonthDays = [];

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(startDate);
      prevDate.setDate(startDate.getDate() - (i + 1));
      prevMonthDays.push(prevDate);
    }

    // 현재 월의 날짜들
    const currentMonthDays = monthDays;

    // 마지막 주를 7일로 맞추기 위해 다음 달의 날짜들 추가
    const lastDayOfWeek = endDate.getDay();
    const nextMonthDays = [];

    for (let i = 1; i <= (6 - lastDayOfWeek); i++) {
      const nextDate = new Date(endDate);
      nextDate.setDate(endDate.getDate() + i);
      nextMonthDays.push(nextDate);
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  const calendarDays = getCalendarDays();

  // 이전 달로 이동
  const prevMonth = () => {
    const newDate = subMonths(currentDate, 1);
    setInternalDate(newDate);
    onMonthChange?.(newDate);
  };

  // 다음 달로 이동
  const nextMonth = () => {
    const newDate = addMonths(currentDate, 1);
    setInternalDate(newDate);
    onMonthChange?.(newDate);
  };

  // 특정 날짜의 이벤트 가져오기 (events 배열에서만 필터링)
  const getDayEvents = (date: Date) => {
    const dayEvents = events.filter(event => isSameDay(new Date(event.start), date));
    // 시간순으로 정렬 (등록 순서와 관계없이 시간 순서대로)
    return dayEvents.sort((a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime()
    );
  };

  // 특정 날짜가 휴일인지 확인하고 휴일 정보 반환
  const getHolidayForDate = (date: Date): Holiday | null => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.find(holiday => {
      const start = holiday.startDate;
      const end = holiday.endDate;
      return dateStr >= start && dateStr <= end;
    }) || null;
  };

  // 휴일 배너 위치 결정 (연속 휴일 연결 티커용)
  const getHolidayPosition = (date: Date, holiday: Holiday): 'single' | 'start' | 'middle' | 'end' => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const startDate = holiday.startDate;
    const endDate = holiday.endDate;

    // 하루짜리 휴일
    if (startDate === endDate) return 'single';

    // 시작일
    if (dateStr === startDate) return 'start';
    // 종료일
    if (dateStr === endDate) return 'end';
    // 중간
    return 'middle';
  };

  // 날짜 클릭 핸들러
  const handleDateClick = (date: Date, dayEvents: CalendarEvent[]) => {
    // 시간대 문제 해결: 명시적으로 로컬 시간으로 새로운 Date 객체 생성
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    onDateClick?.(localDate, dayEvents);
  };

  // 드래그 앤 드롭 핸들러
  const isEventDraggable = (event: CalendarEvent): boolean => {
    const data = event.data as Record<string, any>;
    const scheduleType = data?.scheduleType || '';
    return draggableEventTypes.includes(scheduleType);
  };

  const handleDragStart = (e: React.DragEvent, event: CalendarEvent) => {
    if (!isEventDraggable(event)) {
      e.preventDefault();
      return;
    }
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(event));
  };

  const handleDragEnd = () => {
    setDraggedEvent(null);
    setDragOverDate(null);
  };

  const handleDragOver = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!dragOverDate || !isSameDay(dragOverDate, date)) {
      setDragOverDate(date);
    }
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    setDragOverDate(null);

    // 내부 드래그 또는 외부(사이드바) 드래그 이벤트 확인
    let eventToMove = draggedEvent;
    if (!eventToMove) {
      try {
        const data = e.dataTransfer.getData('application/calendar-event') || e.dataTransfer.getData('text/plain');
        if (data) eventToMove = JSON.parse(data) as CalendarEvent;
      } catch { /* ignore */ }
    }

    if (!eventToMove || !onEventDrop) {
      setDraggedEvent(null);
      return;
    }

    // 같은 날짜에 드롭하면 무시
    if (isSameDay(new Date(eventToMove.start), date)) {
      setDraggedEvent(null);
      return;
    }

    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    onEventDrop(eventToMove, localDate);
    setDraggedEvent(null);
  };

  // 모바일에서 이벤트 제목 단축
  const getShortenedTitle = (title: string) => {
    if (isMobile) {
      const cleanTitle = title.includes('연습실 예약 - ')
        ? title.replace('연습실 예약 - ', '')
        : title.includes('정규 수업 - ')
          ? title.replace('정규 수업 - ', '')
          : title;

      return cleanTitle.length > 8 ? cleanTitle.substring(0, 8) + '...' : cleanTitle;
    }

    return title.includes('연습실 예약 - ')
      ? title.replace('연습실 예약 - ', '')
      : title.includes('정규 수업 - ')
        ? title.replace('정규 수업 - ', '')
        : title;
  };

  return (
    <div className="simple-calendar">
      {/* 헤더 */}
      {!hideHeader && (
        <div className="calendar-header">
          <button
            type="button"
            onClick={prevMonth}
            className="month-nav-btn"
            aria-label="이전 달"
          >
            &lt;
          </button>
          <h2 className="current-month">
            {format(currentDate, 'yyyy년 M월', { locale: ko })}
          </h2>
          <button
            type="button"
            onClick={nextMonth}
            className="month-nav-btn"
            aria-label="다음 달"
          >
            &gt;
          </button>
        </div>
      )}

      {/* 요일 헤더 */}
      <div className="calendar-weekdays">
        {['일', '월', '화', '수', '목', '금', '토'].map(day => (
          <div key={day} className="weekday-header">{day}</div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="calendar-grid">
        {calendarDays.map((day, index) => {
          const dayEvents = getDayEvents(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isToday = isSameDay(day, new Date());
          const isPastDate = isBefore(day, startOfDay(new Date()));
          const holiday = getHolidayForDate(day);
          const holidayInfo = getHolidayInfo(day); // 공휴일/기념일 정보
          const dayOfWeek = day.getDay(); // 0=일요일, 6=토요일

          // 날짜 숫자 색상 결정: 공휴일 > 일요일 > 토요일 > 기본
          const getDayNumberClass = () => {
            if (holidayInfo?.isPublicHoliday) return 'public-holiday'; // 공휴일: 빨강
            if (dayOfWeek === 0) return 'sunday'; // 일요일: 빨강
            if (dayOfWeek === 6) return 'saturday'; // 토요일: 파랑
            return '';
          };

          // 휴일 배너가 있으면 이벤트 1개 덜 표시
          const displayMaxEvents = holiday ? Math.max(maxEvents - 1, 1) : maxEvents;
          const remainingEvents = dayEvents.length - displayMaxEvents;

          return (
            <div
              key={index}
              className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isPastDate ? 'past-date' : ''} ${selectedDate && isSameDay(day, selectedDate) ? 'selected' : ''} ${holiday ? 'is-holiday' : ''} ${holidayInfo?.isPublicHoliday ? 'is-public-holiday' : ''} ${dragOverDate && isSameDay(dragOverDate, day) ? 'drag-over' : ''}`}
              onClick={() => handleDateClick(day, dayEvents)}
              role="button"
              tabIndex={isPastDate ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDateClick(day, dayEvents);
                }
              }}
              onDragOver={(e) => handleDragOver(e, day)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day)}
            >
              {/* 날짜 + 공휴일/기념일 이름 + 더보기 배지 */}
              <div className="day-header">
                <span className={`day-number ${getDayNumberClass()}`}>
                  {format(day, 'd')}
                </span>
                {holidayInfo && !isMobile && (
                  <span
                    className={`special-day-name ${holidayInfo.isPublicHoliday ? 'public-holiday' : 'commemorative'}`}
                    title={holidayInfo.name}
                  >
                    {holidayInfo.name}
                  </span>
                )}
                {/* 더보기 배지: 날짜 오른쪽 끝 */}
                {!isMobile && remainingEvents > 0 && (
                  <span className="more-badge">+{remainingEvents}</span>
                )}
              </div>

              {/* 학원 휴일 배너 (날짜 아래, 연속 휴일 연결) */}
              {holiday && (() => {
                const position = getHolidayPosition(day, holiday);
                return (
                  <div
                    className={`holiday-banner holiday-${position}`}
                    title={holiday.title}
                  >
                    {/* 시작일 또는 단일 휴일만 제목 표시 */}
                    {(position === 'single' || position === 'start') && holiday.title}
                  </div>
                );
              })()}

              {/* 이벤트 표시 */}
              {dayEvents.length > 0 && (
                isMobile ? (
                  // 모바일: 점(dot)으로만 표시
                  <div className="day-events-dots">
                    {dayEvents.slice(0, displayMaxEvents).map((event, eventIndex) => (
                      <span
                        key={eventIndex}
                        className="event-dot"
                        style={{ backgroundColor: event.color }}
                      />
                    ))}
                    {dayEvents.length > displayMaxEvents && (
                      <span className="dots-more">+{dayEvents.length - displayMaxEvents}</span>
                    )}
                  </div>
                ) : (
                  // 데스크톱: dot + 텍스트 표시
                  <div className="day-events">
                    {dayEvents.slice(0, displayMaxEvents).map((event, eventIndex) => {
                      const isDraggable = isEventDraggable(event);
                      const isBeingDragged = draggedEvent && draggedEvent.id === event.id;
                      return (
                        <div
                          key={eventIndex}
                          className={`event-item ${isDraggable ? 'draggable' : ''} ${isBeingDragged ? 'dragging' : ''}`}
                          style={{ '--event-color': event.color } as React.CSSProperties}
                          draggable={isDraggable}
                          onDragStart={(e) => handleDragStart(e, event)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick?.(event);
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            onEventDoubleClick?.(event);
                          }}
                        >
                          <span
                            className="event-dot-indicator"
                            style={{ backgroundColor: event.color }}
                          />
                          <span className="event-time">
                            {format(new Date(event.start), 'HH:mm')}
                          </span>
                          <span className="event-title">
                            {getShortenedTitle(event.title)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SimpleCalendar; 